// Subscription Service - Manages trial, subscriptions, and billing
import { doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc, orderBy, limit } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, COLLECTIONS, functions } from './firebase'
import {
  CompanySubscription,
  BillingRecord,
  SubscriptionState,
  SUBSCRIPTION_PRICING
} from '../types'
import { SubscriptionStatus, BillingCycle, PaymentProvider, PlanTypeEnum } from '../types/enums'

const SUBSCRIPTION_ENFORCEMENT_ENABLED = true

// ============================================
// SUBSCRIPTION HELPERS
// ============================================

// Calculate days remaining until a date
const calculateDaysRemaining = (endDateStr: string): number => {
  const endDate = new Date(endDateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  endDate.setHours(0, 0, 0, 0)
  const diffTime = endDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
}

// Add days to a date
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

// Add months to a date
const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

// Format date to ISO string (date only)
const toISODateString = (date: Date): string => {
  return date.toISOString().split('T')[0]
}

// ============================================
// SUBSCRIPTION CRUD OPERATIONS
// ============================================

// Get subscription for a company
export const getCompanySubscription = async (companyId: string): Promise<CompanySubscription | null> => {
  if (!db || !companyId) return null

  try {
    const directRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, companyId)
    const directDoc = await getDoc(directRef)
    if (directDoc.exists()) {
      return directDoc.data() as CompanySubscription
    }

    const subscriptionsRef = collection(db, COLLECTIONS.SUBSCRIPTIONS)
    const q = query(subscriptionsRef, where('companyId', '==', companyId), limit(1))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return null
    }

    const legacyDoc = querySnapshot.docs[0]
    const legacyData = legacyDoc.data() as CompanySubscription

    await setDoc(directRef, { ...legacyData, id: companyId }, { merge: true })
    return { ...legacyData, id: companyId }
  } catch (error) {
    console.error('Error getting subscription:', error)
    return null
  }
}

// Create a new trial subscription for a company
export const createTrialSubscription = async (companyId: string): Promise<CompanySubscription> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const now = new Date()
  const trialEndDate = addDays(now, SUBSCRIPTION_PRICING.trialDays)
  const subscription: CompanySubscription = {
    id: companyId,
    companyId,
    planType: PlanTypeEnum.SILVER,
    status: SubscriptionStatus.TRIALING,

    // Trial info
    trialStartDate: now.toISOString(),
    trialEndDate: trialEndDate.toISOString(),
    trialDays: SUBSCRIPTION_PRICING.trialDays,

    // Payment
    paymentProvider: PaymentProvider.RAZORPAY,

    // Pricing
    monthlyPrice: SUBSCRIPTION_PRICING.monthly,
    yearlyPrice: SUBSCRIPTION_PRICING.yearly,

    // Settings
    autoRenew: true,
    gracePeriodDays: SUBSCRIPTION_PRICING.gracePeriodDays,

    // Metadata
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  }

  const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, companyId)
  await setDoc(subscriptionRef, subscription)

  return subscription
}

// Get or create subscription (ensures every company has a subscription)
export const getOrCreateSubscription = async (companyId: string): Promise<CompanySubscription> => {
  const existing = await getCompanySubscription(companyId)
  if (existing) {
    return existing
  }
  return createTrialSubscription(companyId)
}

// Update subscription status based on dates
export const updateSubscriptionStatus = async (subscription: CompanySubscription): Promise<CompanySubscription> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const now = new Date()
  let newStatus = subscription.status
  let endDate: Date

  // Determine the end date based on subscription status
  if (subscription.status === SubscriptionStatus.TRIALING) {
    endDate = new Date(subscription.trialEndDate)
  } else if (subscription.subscriptionEndDate) {
    endDate = new Date(subscription.subscriptionEndDate)
  } else {
    endDate = new Date(subscription.trialEndDate)
  }

  // Check if trial/subscription has expired
  if (now > endDate) {
    const gracePeriodEnd = addDays(endDate, subscription.gracePeriodDays)

    if (now <= gracePeriodEnd) {
      newStatus = SubscriptionStatus.GRACE_PERIOD
    } else {
      newStatus = SubscriptionStatus.EXPIRED
    }
  }

  const remainingDays = calculateDaysRemaining(endDate.toISOString())

  // Only update if status changed
  if (newStatus !== subscription.status || subscription.remainingDays !== remainingDays) {
    const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, subscription.id)
    await updateDoc(subscriptionRef, {
      status: newStatus,
      remainingDays,
      updatedAt: now.toISOString()
    })
    subscription.status = newStatus
    subscription.remainingDays = remainingDays
    subscription.updatedAt = now.toISOString()
  }

  return subscription
}

// ============================================
// SUBSCRIPTION STATE HELPERS
// ============================================

// Get computed subscription state for UI
export const getSubscriptionState = (
  subscription: CompanySubscription | null,
  userRole?: string
): SubscriptionState => {
  const isSuperAdmin = userRole === 'super_admin'
  if (!subscription) {
    if (isSuperAdmin) {
      return {
        isActive: true,
        isTrialing: false,
        isExpired: false,
        isGracePeriod: false,
        daysRemaining: 365,
        canEdit: true,
        showExpiryWarning: false,
        subscription: null
      }
    }
    return {
      isActive: false,
      isTrialing: false,
      isExpired: true,
      isGracePeriod: false,
      daysRemaining: 0,
      canEdit: false,
      showExpiryWarning: true,
      subscription: null
    }
  }

  const status = subscription.status
  const isTrialing = status === SubscriptionStatus.TRIALING
  const isActive = status === SubscriptionStatus.ACTIVE
  const isGracePeriod = status === SubscriptionStatus.GRACE_PERIOD
  const isExpired = status === SubscriptionStatus.EXPIRED || status === SubscriptionStatus.CANCELLED || isGracePeriod

  // Calculate days remaining
  let daysRemaining = 0
  if (isTrialing) {
    daysRemaining = calculateDaysRemaining(subscription.trialEndDate)
  } else if (subscription.subscriptionEndDate) {
    daysRemaining = calculateDaysRemaining(subscription.subscriptionEndDate)
  }

  // Can edit: only if not expired
  const canEdit = !isExpired

  // Show warning during the last week before subscription/trial expiry
  const showExpiryWarning = daysRemaining <= 7 && daysRemaining >= 0

  const state = {
    isActive,
    isTrialing,
    isExpired,
    isGracePeriod,
    daysRemaining,
    canEdit,
    showExpiryWarning,
    subscription
  }

  if (!SUBSCRIPTION_ENFORCEMENT_ENABLED) {
    return {
      ...state,
      isActive: true,
      isTrialing: false,
      isExpired: false,
      isGracePeriod: false,
      daysRemaining: Math.max(daysRemaining, 365),
      canEdit: true,
      showExpiryWarning: false
    }
  }

  if (isSuperAdmin) {
    return {
      ...state,
      isActive: true,
      isTrialing: false,
      isExpired: false,
      isGracePeriod: false,
      daysRemaining: Math.max(daysRemaining, 365),
      canEdit: true,
      showExpiryWarning: false
    }
  }

  return state
}

// ============================================
// PAYMENT & UPGRADE FUNCTIONS
// ============================================

// Activate subscription after payment
export const activateSubscription = async (
  subscriptionId: string,
  billingCycle: BillingCycle,
  razorpayPaymentId?: string,
  razorpayOrderId?: string,
  planType?: PlanTypeEnum
): Promise<CompanySubscription> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, subscriptionId)
  const subscriptionDoc = await getDoc(subscriptionRef)

  if (!subscriptionDoc.exists()) {
    throw new Error('Subscription not found')
  }

  const subscription = subscriptionDoc.data() as CompanySubscription
  const now = new Date()

  const currentEndDate = subscription.subscriptionEndDate
    ? new Date(subscription.subscriptionEndDate)
    : now
  const startDate = currentEndDate > now ? currentEndDate : now
  const subscriptionEndDate = addMonths(startDate, billingCycle === BillingCycle.YEARLY ? 12 : 1)

  const amount = billingCycle === BillingCycle.YEARLY ? 6000 : 500

  // Update subscription
  const updatedData = {
    status: SubscriptionStatus.ACTIVE,
    billingCycle,
    planType: planType || subscription.planType,
    subscriptionStartDate: now.toISOString(),
    subscriptionEndDate: subscriptionEndDate.toISOString(),
    nextBillingDate: subscriptionEndDate.toISOString(),
    lastPaymentDate: now.toISOString(),
    lastPaymentAmount: amount,
    updatedAt: now.toISOString()
  }

  await updateDoc(subscriptionRef, updatedData)

  // Create billing record
  await createBillingRecord({
    companyId: subscription.companyId,
    subscriptionId: subscription.id,
    amount,
    currency: 'INR',
    billingDate: now.toISOString(),
    dueDate: now.toISOString(),
    paidDate: now.toISOString(),
    status: 'paid',
    razorpayPaymentId,
    razorpayOrderId,
    paymentMethod: 'razorpay',
    billingCycle,
    planType: planType || subscription.planType
  })

  return { ...subscription, ...updatedData } as CompanySubscription
}

// Extend subscription (for renewals)
export const extendSubscription = async (
  subscriptionId: string,
  billingCycle: BillingCycle
): Promise<CompanySubscription> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, subscriptionId)
  const subscriptionDoc = await getDoc(subscriptionRef)

  if (!subscriptionDoc.exists()) {
    throw new Error('Subscription not found')
  }

  const subscription = subscriptionDoc.data() as CompanySubscription
  const now = new Date()

  // Calculate new end date (from current end date or now, whichever is later)
  const currentEndDate = subscription.subscriptionEndDate
    ? new Date(subscription.subscriptionEndDate)
    : now
  const startDate = currentEndDate > now ? currentEndDate : now

  const newEndDate = billingCycle === BillingCycle.MONTHLY
    ? addMonths(startDate, 1)
    : addMonths(startDate, 12)

  const updatedData = {
    status: SubscriptionStatus.ACTIVE,
    subscriptionEndDate: newEndDate.toISOString(),
    nextBillingDate: newEndDate.toISOString(),
    updatedAt: now.toISOString()
  }

  await updateDoc(subscriptionRef, updatedData)

  return { ...subscription, ...updatedData } as CompanySubscription
}

// Cancel subscription
export const cancelSubscription = async (
  subscriptionId: string,
  reason?: string
): Promise<void> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, subscriptionId)
  const now = new Date()

  await updateDoc(subscriptionRef, {
    status: SubscriptionStatus.CANCELLED,
    autoRenew: false,
    cancelledAt: now.toISOString(),
    cancelReason: reason || 'User cancelled',
    updatedAt: now.toISOString()
  })
}

// Update subscription plan without billing (manual downgrade/upgrade adjustments)
export const updateSubscriptionPlan = async (
  subscriptionId: string,
  planType: PlanTypeEnum
): Promise<CompanySubscription> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, subscriptionId)
  const subscriptionDoc = await getDoc(subscriptionRef)

  if (!subscriptionDoc.exists()) {
    throw new Error('Subscription not found')
  }

  const subscription = subscriptionDoc.data() as CompanySubscription
  const now = new Date()

  const updatedData = {
    planType,
    updatedAt: now.toISOString()
  }

  await updateDoc(subscriptionRef, updatedData)

  return { ...subscription, ...updatedData } as CompanySubscription
}

// ============================================
// BILLING RECORDS
// ============================================

// Create a billing record
export const createBillingRecord = async (data: Omit<BillingRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<BillingRecord> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const now = new Date()
  const billingId = `bill_${data.companyId}_${Date.now()}`

  const billingRecord: BillingRecord = {
    ...data,
    id: billingId,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString()
  }

  const billingRef = doc(db, COLLECTIONS.BILLING_RECORDS, billingId)
  await setDoc(billingRef, billingRecord)

  return billingRecord
}

// Get billing history for a company
export const getBillingHistory = async (companyId: string, limitCount: number = 10): Promise<BillingRecord[]> => {
  if (!db || !companyId) return []

  try {
    const billingRef = collection(db, COLLECTIONS.BILLING_RECORDS)
    try {
      const q = query(
        billingRef,
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      )
      const querySnapshot = await getDocs(q)

      return querySnapshot.docs.map(doc => doc.data() as BillingRecord)
    } catch (error: any) {
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        const q = query(
          billingRef,
          where('companyId', '==', companyId),
          limit(limitCount * 3)
        )
        const querySnapshot = await getDocs(q)
        const records = querySnapshot.docs.map(doc => doc.data() as BillingRecord)
        records.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        return records.slice(0, limitCount)
      }
      throw error
    }
  } catch (error) {
    console.error('Error getting billing history:', error)
    return []
  }
}

// ============================================
// TRIAL HELPERS
// ============================================

// Check if trial has expired
export const isTrialExpired = (subscription: CompanySubscription): boolean => {
  if (subscription.status !== SubscriptionStatus.TRIALING) {
    return false
  }
  const trialEnd = new Date(subscription.trialEndDate)
  return new Date() > trialEnd
}

// Get trial days remaining
export const getTrialDaysRemaining = (subscription: CompanySubscription): number => {
  if (subscription.status !== SubscriptionStatus.TRIALING) {
    return 0
  }
  return calculateDaysRemaining(subscription.trialEndDate)
}

// Extend trial (admin function)
export const extendTrial = async (subscriptionId: string, additionalDays: number): Promise<void> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, subscriptionId)
  const subscriptionDoc = await getDoc(subscriptionRef)

  if (!subscriptionDoc.exists()) {
    throw new Error('Subscription not found')
  }

  const subscription = subscriptionDoc.data() as CompanySubscription
  const currentEndDate = new Date(subscription.trialEndDate)
  const newEndDate = addDays(currentEndDate, additionalDays)

  await updateDoc(subscriptionRef, {
    trialEndDate: newEndDate.toISOString(),
    trialDays: subscription.trialDays + additionalDays,
    updatedAt: new Date().toISOString()
  })
}

// ============================================
// RAZORPAY INTEGRATION HELPERS
// ============================================

// Generate Razorpay order for subscription payment
export interface RazorpayOrderData {
  amount: number  // in paise
  currency: string
  receipt: string
  notes: {
    companyId: string
    subscriptionId: string
    billingCycle: BillingCycle
    planType: PlanTypeEnum
    months?: number
  }
}

export const generateSubscriptionOrderData = (
  subscription: CompanySubscription,
  billingCycle: BillingCycle,
  months: number = 1
): RazorpayOrderData => {
  const normalizedMonths = Math.min(36, Math.max(1, Math.floor(months)))
  const amount = 500 * normalizedMonths

  return {
    amount: amount * 100, // Convert to paise
    currency: 'INR',
    receipt: `receipt_${subscription.id}_${Date.now()}`,
    notes: {
      companyId: subscription.companyId,
      subscriptionId: subscription.id,
      billingCycle,
      planType: subscription.planType,
      months: normalizedMonths
    }
  }
}

export interface RazorpaySubscriptionOrder {
  orderId: string
  amount: number
  currency: string
  keyId: string
}

export interface RazorpaySubscriptionVerificationResult {
  valid: boolean
  subscriptionId: string
  subscriptionEndDate: string
}

const callSubscriptionPaymentApi = async <T>(path: string, payload: Record<string, unknown>): Promise<T> => {
  const token = await auth?.currentUser?.getIdToken()
  if (!token) {
    throw new Error('Please sign in again before making payment.')
  }

  const response = await fetch(`/api/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  })

  const responseText = await response.text()
  let result: any = {}
  try {
    result = responseText ? JSON.parse(responseText) : {}
  } catch {
    result = {}
  }

  if (!response.ok || result?.error) {
    const message =
      result?.error?.message ||
      result?.message ||
      responseText ||
      `Payment setup failed. Server returned ${response.status}.`
    throw new Error(message)
  }

  return (result?.data || result) as T
}

export const createSubscriptionOrder = async (
  subscription: CompanySubscription,
  billingCycle: BillingCycle,
  months: number = 1
): Promise<RazorpaySubscriptionOrder> => {
  if (!functions) {
    throw new Error('Payment gateway not initialized')
  }

  return callSubscriptionPaymentApi<RazorpaySubscriptionOrder>('createRazorpaySubscriptionOrder', {
    companyId: subscription.companyId,
    subscriptionId: subscription.id,
    billingCycle,
    planType: subscription.planType,
    months
  })
}

export const verifyAndActivateSubscriptionPayment = async (params: {
  orderId: string
  paymentId: string
  signature: string
  subscriptionId: string
  billingCycle: BillingCycle
  planType: PlanTypeEnum
  months?: number
}): Promise<RazorpaySubscriptionVerificationResult> => {
  if (!functions) {
    throw new Error('Payment gateway not initialized')
  }

  return callSubscriptionPaymentApi<RazorpaySubscriptionVerificationResult>('verifyRazorpaySubscriptionPayment', params)
}

// Verify Razorpay payment signature (should be done on backend for security)
export const verifyPaymentSignature = async (
  orderId: string,
  paymentId: string,
  signature: string,
  subscriptionId: string,
  billingCycle: BillingCycle,
  planType: PlanTypeEnum,
  months: number = 1
): Promise<boolean> => {
  const result = await verifyAndActivateSubscriptionPayment({
    orderId,
    paymentId,
    signature,
    subscriptionId,
    billingCycle,
    planType,
    months
  })
  return result.valid === true
}
